<script>
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import { Helper } from '@/mixin/helper'
import {
  GET_PART_TYPES,
  GET_PART_TYPE_BY_ID,
  CREATE_PART_TYPE,
  UPDATE_PART_TYPE,
  DELETE_PART_TYPE,
} from '@/services/api/setting/maintenance/parts/type'
import { GET_PART_CATEGORIES } from '@/services/api/setting/maintenance/parts/categories'

export default {
  components: { McGraphqlEditableTable },
  mixins: [Helper],
  props: {
    status: String,
  },
  data() {
    return {
      data: [],
    }
  },

  computed: {
    fields() {
      return [
        {
          key: 'type',
          label: this.$t('setting.list.maintenance.partsType'),
          placeholder: this.$t('setting.list.maintenance.partsType'),
          thClass: 'mc-table--500',
          isSortable: false,
          editable: true,
          validation: [['required', 'trim']],
        },
        {
          key: 'partCategory',
          label: this.$t('setting.list.maintenance.partsCategory'),
          placeholder: this.$t('setting.list.maintenance.partsCategory'),
          thClass: 'mc-table--500',
          isSortable: false,
          editable: true,
          type: 'select-resourceful',
          validation: [['required']],
          customColumn: true,
          trackBy: 'partCategoryId',
          optionsGetter: this.resourcefulQuery.partCategory,
          optionLabelFn: ({ item }) =>
            item.partCategory ? item.partCategory.category : item.category,
          getInitialObject: (item) => {
            return {
              category: item?.partCategory?.category,
              partCategoryId: item?.partCategoryId,
            }
          },
          customValueFn: (item) => item.partCategoryId ?? '',
          thStyle: 'width: 500px',
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_PART_TYPES,
            variables: {
              filter: filter,
            },
          })

          return res.data.partTypes.partTypes
        },
        store: async (data) => {
          const input = {
            partCategoryId: data.partCategory,
            type: data.type,
          }

          const res = await this.$apollo.mutate({
            mutation: CREATE_PART_TYPE,
            fetchPolicy: 'no-cache',
            variables: {
              input: input,
            },
          })

          if (!res.data.createPartType.isSuccessful) {
            if (res.data.createPartType.errorName === 'PartTypeAlreadyExist')
              this.$mcToast.error(
                this.$t('setting.list.maintenance.alert.' + res.data.createPartType.errorName)
              )
            return false
          } else {
            const newData = await this.$apollo.query({
              query: GET_PART_TYPE_BY_ID,
              fetchPolicy: 'no-cache',
              variables: {
                filter: {
                  partTypeId: res.data.createPartType.createPartType.partTypeId,
                },
              },
            })

            return newData.data.partType.partType
          }
        },
        update: async (id, oldData, newData) => {
          const input = {
            partTypeId: id,
            oldData: {
              partCategoryId: oldData.partCategoryId,
              type: oldData.type,
            },
            newData: {
              partCategoryId: newData.partCategory,
              type: newData.type,
            },
          }

          const res = await this.$apollo.mutate({
            mutation: UPDATE_PART_TYPE,
            fetchPolicy: 'no-cache',
            variables: {
              input: input,
            },
          })

          if (!res.data.updatePartType.isSuccessful) {
            if (res.data.updatePartType.errorName === 'PartTypeAlreadyExist')
              this.$mcToast.error(
                this.$t('setting.list.maintenance.alert.' + res.data.updatePartType.errorName)
              )
            else {
              this.$mcToast.error({
                component: 'McToastDynamicBody',
                props: {
                  contents: [
                    {
                      tag: 'h6',
                      text: this.$t('maintenance.alert.editFailed'),
                    },
                    {
                      tag: 'p',
                      text: this.$t(`global.alert.text.failedEdit`),
                    },
                  ],
                },
              })
            }
            return false
          }

          if (!res.data.updatePartType.updatePartType) return true
          return res.data.updatePartType.updatePartType
        },
        delete: async (id) => {
          const input = {
            partTypeId: +id,
          }

          const res = await this.$apollo.mutate({
            fetchPolicy: 'no-cache',
            mutation: DELETE_PART_TYPE,
            variables: {
              input: input,
            },
          })

          return res.data.deletePartType.isSuccessful
        },
      }
    },

    resourcefulQuery() {
      return {
        partCategory: async (opts) => {
          let result = {}
          if (opts.page !== 1) {
            result = {
              metadata: {
                total_page: 1,
                total_count: 0,
                page: 1,
              },
              data: {
                paginated_result: [],
              },
            }
          } else {
            const filter = {
              pagination: {
                page: opts.page,
                show: 99999,
              },
              search: opts.search,
            }

            const res = await this.$apollo.query({
              query: GET_PART_CATEGORIES,
              fetchPolicy: 'no-cache',
              variables: {
                filter: filter,
              },
            })

            const data = res.data.partCategories.partCategories

            result = {
              metadata: {
                total_page: data.pagination.total,
                total_count: data.pagination.total,
                page: data.pagination.page,
              },
              data: {
                paginated_result: data.data,
              },
            }
          }

          return result
        },
      }
    },
  },

  methods: {
    handleClick() {
      this.$router.push({ name: 'Log Maintenance Setting', params: { type: 'category' } })
    },

    customToast() {
      this.$mcToast.error({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              tag: 'h6',
              text: this.$t('maintenance.alert.deleteFailed'),
            },
            {
              tag: 'p',
              text: this.$t(`setting.list.maintenance.alert.ResourceBeingUsedInMaster`),
            },
          ],
        },
      })
    },
  },
}
</script>

<template>
  <!-- Rekrutmen -->
  <b-tab class="border-0 pb-4" @click="$store.commit('setting/SET_STATUS', status)">
    <template v-slot:title>
      <span class="d-sm-inline-block">{{ $t('setting.list.maintenance.partsType') }}</span>
    </template>

    <!-- Table -->
    <McGraphqlEditableTable
      :fields="fields"
      :apiClient="query"
      trackBy="partTypeId"
      :searchable="true"
      :customToast="customToast"
    >
      <template #partCategory="data">
        {{ data.value.category }}
      </template>
    </McGraphqlEditableTable>
  </b-tab>
</template>
